import type { VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import * as AvatarPrimitive from '@radix-ui/react-avatar';
import { cn } from '@repo/lib/cn';
import { cva } from 'class-variance-authority';
import { ImagePlusIcon } from 'lucide-react';

export const avatarVariants = cva('group/avatar bg-accent border-background relative flex shrink-0 overflow-hidden rounded-md shadow-xs [&_svg]:pointer-events-none [&_svg]:shrink-0', {
  variants: {
    size: {
      xs: 'size-4.5 text-xs [&_svg]:size-3',
      sm: 'size-7 text-xs [&_svg]:size-4',
      md: 'size-10 text-sm [&_svg]:size-4',
      lg: 'text-md size-11 [&_svg]:size-5',
      xl: 'text-md size-12 [&_svg]:size-5',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

export function Avatar(avatarProps: ComponentProps<typeof AvatarPrimitive.Root> & VariantProps<typeof avatarVariants>) {
  const { className, size, ...props } = avatarProps;
  return <AvatarPrimitive.Root data-slot="avatar" className={cn(avatarVariants({ size }), className)} {...props} />;
}

export function AvatarImage(avatarImageProps: ComponentProps<typeof AvatarPrimitive.Image>) {
  const { className, ...props } = avatarImageProps;
  return <AvatarPrimitive.Image data-slot="avatar-image" className={cn('aspect-square size-full', className)} {...props} />;
}

export function AvatarFallback(avatarFallbackProps: ComponentProps<typeof AvatarPrimitive.Fallback>) {
  const { className, ...props } = avatarFallbackProps;
  return <AvatarPrimitive.Fallback data-slot="avatar-fallback" className={cn('text-muted-foreground flex h-full w-full items-center justify-center', className)} {...props} />;
}

export function AvatarEdit(avatarEditProps: ComponentProps<typeof AvatarPrimitive.Root> & VariantProps<typeof avatarVariants>) {
  const { className, size, children, ...props } = avatarEditProps;
  return (
    <AvatarPrimitive.Root
      data-slot="avatar-edit"
      role={'button'}
      tabIndex={0}
      className={cn(
        avatarVariants({ size }),
        'group bg-accent hover:bg-accent/80 border-background outline-focus-ring relative cursor-pointer outline-0 transition-all duration-150 ease-out focus-visible:outline-3 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
        className
      )}
      {...props}
    >
      <span className={'bg-gray/60 hover:bg-gray/75 absolute inset-0 m-auto grid size-7 place-items-center rounded-full transition-colors duration-100'}>
        <ImagePlusIcon className={'text-gray-foreground pointer-events-none'} />
      </span>
      {children}
    </AvatarPrimitive.Root>
  );
}
